import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { config } from '@/config';

// it's defined here to solve a circular dependency issue
function deep_copy(o: any) {
  return JSON.parse(JSON.stringify(o));
}

// ✅ config extension (keeps all standard options)
type AxiosConfigWithAuth<D = any> = AxiosRequestConfig<D> & {
  skipAuthRedirect?: boolean;
};

// ✅ instance that accepts AxiosConfigWithAuth in calls
type AxiosWithAuthInstance = AxiosInstance & {
  request<T = any, D = any>(config: AxiosConfigWithAuth<D>): Promise<T>;
  get<T = any, D = any>(
    url: string,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  delete<T = any, D = any>(
    url: string,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  head<T = any, D = any>(
    url: string,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  options<T = any, D = any>(
    url: string,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  post<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  put<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
  patch<T = any, D = any>(
    url: string,
    data?: D,
    config?: AxiosConfigWithAuth<D>
  ): Promise<T>;
};

function detectLang(): string {
  const supported = new Set(['nl', 'en', 'fr', 'es', 'de']);

  const seg = window.location.pathname.split('/')[1];
  if (supported.has(seg)) return seg;

  const stored = (localStorage.getItem('i18nextLng') || '').slice(0, 2);
  if (supported.has(stored)) return stored;

  return 'nl';
}

function toLoginUrl(reason?: string) {
  const lang = detectLang();
  const r = reason ? encodeURIComponent(reason) : '';
  return r ? `/${lang}/login?reason=${r}` : `/${lang}/login`;
}

function isAuthPagePath(pathname: string) {
  return /(^|\/)(nl|en|fr|es)?\/?(login|custom-login|register|forgot-password)(\/|$)/.test(
    pathname
  );
}

/**
 * ✅ Treat "null"/"undefined"/"" as missing
 */
function readStoredToken(key: 'token' | 'access'): string | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  const v = raw.trim();
  if (!v || v === 'null' || v === 'undefined') return null;

  return v;
}

// do not configure auth header
const publicAxios = axios.create();
publicAxios.defaults.headers = deep_copy(axios.defaults.headers);
publicAxios.defaults.headers.common['Cache-Control'] = 'no-cache';

const stAxios = axios.create();
stAxios.defaults.headers = deep_copy(axios.defaults.headers);

// ✅ one-line change: cast the instance
const privateAxios = axios.create() as AxiosWithAuthInstance;
privateAxios.defaults.headers = deep_copy(axios.defaults.headers);
privateAxios.defaults.withCredentials = true;

// ✅ read & sanitize
const token = readStoredToken('token');
const access = readStoredToken('access');

// ✅ only set if valid
if (token)
  privateAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
else delete privateAxios.defaults.headers.common['Authorization'];

if (access)
  privateAxios.defaults.headers.common['Authorization2'] = `Bearer ${access}`;
else delete privateAxios.defaults.headers.common['Authorization2'];

privateAxios.interceptors.request.use((req) => {
  const t = readStoredToken('token');
  const a = readStoredToken('access');

  if (t) {
    req.headers = req.headers || {};
    req.headers['Authorization'] = `Bearer ${t}`;
  } else if (req.headers && 'Authorization' in req.headers) {
    delete (req.headers as any)['Authorization'];
  }

  if (a) {
    req.headers = req.headers || {};
    req.headers['Authorization2'] = `Bearer ${a}`;
  } else if (req.headers && 'Authorization2' in req.headers) {
    delete (req.headers as any)['Authorization2'];
  }

  return req;
});

let logoutInProgress = false;

async function logout(reason?: string | undefined) {
  if (isAuthPagePath(window.location.pathname)) {
    localStorage.removeItem('token');
    localStorage.removeItem('access');
    delete privateAxios.defaults.headers.common['Authorization'];
    delete privateAxios.defaults.headers.common['Authorization2'];
    return;
  }

  if (logoutInProgress) return;
  logoutInProgress = true;

  try {
    await privateAxios.post(`${config.backend}/auth/logout`);
  } catch (e) {
    // ignore
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('access');
    delete privateAxios.defaults.headers.common['Authorization'];
    delete privateAxios.defaults.headers.common['Authorization2'];
    window.location.replace(toLoginUrl(reason));
  }
}

const logOutReasons = new Set([
  //'token_expired', //TODO: re-enable but check agristo login
  'invalid_token',
  'token_revoked',
  //'token_blacklisted', //this can happen due to timing, just ignore it
]);

privateAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ✅ prefer error.config
    const cfg = (error?.config ?? {}) as AxiosConfigWithAuth;
    if (cfg.skipAuthRedirect) {
      return Promise.reject(error);
    }

    const res = error?.response;
    if (!res) return Promise.reject(error);

    if (res?.logged_in) {
      return Promise.reject(error);
    }

    const code = res.data?.code as string | undefined;
    const url = (cfg.url ?? '') as string;

    if (logoutInProgress || url.includes('/auth/logout')) {
      return Promise.reject(error);
    }

    // ✅ Only force logout for known reasons
    if (!code || !logOutReasons.has(code)) {
      return Promise.reject(error);
    }

    console.warn('[AUTH] Session invalid:', code);
    await logout(code);
    return;
  }
);

config.policy = new Promise((resolve, reject) => {
  privateAxios
    .post(
      `${config.backend}/document/policy`,
      { path: 'froala/' },
      { skipAuthRedirect: true }
    )
    .then((result: any) => resolve(result.data))
    .catch(reject);
});

export { privateAxios as axios, publicAxios, stAxios, deep_copy, logout };
